import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { API as BASE_API } from "@/config";

const API = `${BASE_API}/auth`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshingRef = useRef(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Re-fetches the org (name, contact_email, brand_color, plan, etc.).
  // Exposed via context so pages that edit org settings (e.g. Billing's
  // brand colour picker) can refresh the shared copy Layout.js reads for
  // theming, without waiting for a full reload.
  const refreshOrg = async () => {
    if (!user || !user.org_id) {
      setOrg(null);
      return;
    }
    try {
      const { data } = await axios.get(`${BASE_API}/organizations/me`, { withCredentials: true });
      setOrg(data);
    } catch {
      setOrg(null);
    }
  };

  // Fetch the org once we know who's logged in and they actually have one
  // (a user mid-onboarding won't yet) — used to default things like club
  // name to the org's name instead of making people retype it.
  useEffect(() => {
    refreshOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org_id]);

  // Setup axios interceptor for auto-refresh on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes("/auth/login") &&
          !originalRequest.url?.includes("/auth/refresh")
        ) {
          originalRequest._retry = true;
          const refreshed = await tryRefresh();
          if (refreshed) {
            return axios(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const tryRefresh = async () => {
    if (refreshingRef.current) return false;
    refreshingRef.current = true;
    try {
      const { data } = await axios.post(`${API}/refresh`, {}, { withCredentials: true });
      setUser(data);
      return true;
    } catch {
      setUser(false);
      return false;
    } finally {
      refreshingRef.current = false;
    }
  };

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/me`, { withCredentials: true });
      setUser(data);
    } catch {
      // Access token expired or missing — try refresh
      const refreshed = await tryRefresh();
      if (!refreshed) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/logout`, {}, { withCredentials: true });
    setUser(false);
    window.location.href = "/";
  };

  // Email/password sign-in. Throws (with the backend's error message) on
  // failure so the login form can show it inline.
  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const signup = async (email, password, name) => {
    const { data } = await axios.post(`${API}/signup`, { email, password, name }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const requestPasswordReset = async (email) => {
    const { data } = await axios.post(`${API}/forgot-password`, { email }, { withCredentials: true });
    return data;
  };

  const resetPassword = async (token, password) => {
    const { data } = await axios.post(`${API}/reset-password`, { token, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const requestMagicLink = async (email) => {
    const { data } = await axios.post(`${API}/magic-link/request`, { email }, { withCredentials: true });
    return data;
  };

  // Called right after onboarding creates an org, so the app doesn't bounce
  // back to /welcome before the next full checkAuth() would pick it up.
  const setOrgId = (orgId) => {
    setUser((prev) => (prev ? { ...prev, org_id: orgId } : prev));
  };

  return (
    <AuthContext.Provider value={{
      user, org, loading, logout, setOrgId, refreshOrg,
      login, signup, requestPasswordReset, resetPassword, requestMagicLink,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
